import React, { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import ApiService from "../services/apiService";
import socketService from "../services/socketService";

export default function BookingManangement() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isMobileDetailView, setIsMobileDetailView] = useState(false);
  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 767;
  });
  const [pendingCount, setPendingCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [cancelledCount, setCancelledCount] = useState(0);

  const arraysEqualByIds = (a = [], b = []) => {
    if (a.length !== b.length) return false;
    const getId = (x) => x.id ?? x.booking_id;
    for (let i = 0; i < a.length; i++) {
      if (getId(a[i]) !== getId(b[i])) return false;
    }
    return true;
  };

  const normalizeBookingStatus = (status = '') => {
    const normalized = String(status || '').trim().toLowerCase();

    if (['cancelled', 'canceled', 'declined', 'rejected'].includes(normalized)) return 'cancelled';
    if (['pending', 'waiting'].includes(normalized)) return 'pending';
    if (['confirmed', 'approved'].includes(normalized)) return 'confirmed';
    if (['processing', 'in progress', 'in_progress'].includes(normalized)) return 'processing';
    if (['completed', 'done'].includes(normalized)) return 'completed';

    return normalized || 'pending';
  };

  const loadBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const bookingsData = await ApiService.getBookings();
      setBookings((prev) => {
        const next = bookingsData || [];
        return arraysEqualByIds(prev, next) ? prev : next;
      });

      const pending = (bookingsData || []).filter((b) => normalizeBookingStatus(b.booking_status || b.status) === 'pending').length;
      const completed = (bookingsData || []).filter((b) => normalizeBookingStatus(b.booking_status || b.status) === 'completed').length;
      const cancelled = (bookingsData || []).filter((b) => ['cancelled', 'rejected', 'declined'].includes(normalizeBookingStatus(b.booking_status || b.status))).length;

      setPendingCount(pending);
      setCompletedCount(completed);
      setCancelledCount(cancelled);
    } catch (err) {
      setError('Failed to load bookings data');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  useEffect(() => {
    socketService.connect('superadmin');
    const handleCreated = () => loadBookings();
    const handleUpdated = () => loadBookings();
    const handleDeleted = () => loadBookings();

    socketService.onBookingCreated(handleCreated);
    socketService.onBookingUpdated(handleUpdated);
    socketService.onBookingDeleted(handleDeleted);

    return () => {
      socketService.offBookingCreated(handleCreated);
      socketService.offBookingUpdated(handleUpdated);
      socketService.offBookingDeleted(handleDeleted);
    };
  }, [loadBookings]);

  const totalBookings = bookings.length;

  const filteredBookings = bookings.filter((booking) => {
    const term = searchTerm.toLowerCase();
    const statusValue = normalizeBookingStatus(booking.booking_status || booking.status || 'pending');

    const matchesSearch = !searchTerm ||
      (booking.customer_first_name && booking.customer_first_name.toLowerCase().includes(term)) ||
      (booking.customer_last_name && booking.customer_last_name.toLowerCase().includes(term)) ||
      (booking.shop_name && booking.shop_name.toLowerCase().includes(term)) ||
      (booking.service_name && booking.service_name.toLowerCase().includes(term)) ||
      (booking.booking_id && String(booking.booking_id).toLowerCase().includes(term));

    const matchesFilter = filterStatus === 'all' || statusValue === filterStatus;

    return matchesSearch && matchesFilter;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 767);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleView = async (booking, forcePageView = false) => {
    setIsLoadingDetails(true);
    try {
      const details = await ApiService.getBookingById(booking.id || booking.booking_id);
      setSelectedBooking(details);
      setIsMobileDetailView(forcePageView || isMobileView);
    } catch (err) {
      setError('Failed to load booking details');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleBackToList = () => {
    setSelectedBooking(null);
    setIsMobileDetailView(false);
  };

  const formatAmount = (value) => Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const formatBookingDate = (booking) => {
    const value = booking.service_date || booking.booking_date || booking.pickup_date;
    if (!value) return 'N/A';

    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
    const date = dateOnlyMatch
      ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
      : new Date(value);

    return Number.isNaN(date.getTime())
      ? 'N/A'
      : date.toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusMeta = (status = 'pending') => {
    const normalizedStatus = String(status || 'pending').toLowerCase();

    if (normalizedStatus === 'pending') {
      return { label: 'Pending', bg: '#fef3c7', color: '#92400e', border: '#fcd34d' };
    }

    if (normalizedStatus === 'confirmed') {
      return { label: 'Confirmed', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' };
    }

    if (normalizedStatus === 'processing') {
      return { label: 'Processing', bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' };
    }

    if (normalizedStatus === 'completed') {
      return { label: 'Completed', bg: '#ecfdf5', color: '#047857', border: '#bbf7d0' };
    }

    if (normalizedStatus === 'cancelled' || normalizedStatus === 'canceled') {
      return { label: 'Cancelled', bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' };
    }

    if (normalizedStatus === 'rejected') {
      return { label: 'Rejected', bg: '#fef2f2', color: '#991b1b', border: '#fecaca' };
    }

    if (normalizedStatus === 'paid') {
      return { label: 'Paid', bg: '#ecfeff', color: '#0f766e', border: '#a7f3d0' };
    }

    return {
      label: normalizedStatus
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' '),
      bg: '#f8fafc',
      color: '#475569',
      border: '#e2e8f0',
    };
  };

  const renderBookingDetails = (booking) => {
    const bookingStatus = booking.booking_status || booking.status || 'pending';
    const statusMeta = getStatusMeta(bookingStatus);

    return (
      <div style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '18px',
        padding: '24px',
        boxShadow: '0 16px 32px rgba(15, 23, 42, 0.06)',
        marginTop: '18px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '18px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: '24px', color: '#0f172a' }}>Booking Details</h3>
          <span className="booking-row-status-badge" style={{
            background: statusMeta.bg,
            color: statusMeta.color,
            borderColor: statusMeta.border,
          }}>
            {statusMeta.label}
          </span>
        </div>

        <div style={{ display: 'grid', gap: '14px', color: '#334155', fontSize: '14px' }}>
          <div><strong>Customer:</strong> {booking.customer_first_name || ''} {booking.customer_last_name || ''}</div>
          <div><strong>Shop:</strong> {booking.shop_name || 'N/A'}</div>
          <div><strong>Service:</strong> {booking.service_name || 'N/A'}</div>
          <div><strong>Booking Date:</strong> {formatBookingDate(booking)}</div>
          <div><strong>Reference:</strong> {booking.payment_reference || booking.booking_id || booking.id || 'N/A'}</div>
          <div><strong>Status:</strong> {statusMeta.label}</div>
          <div><strong>Amount:</strong> ₱{Number(booking.total_amount || booking.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
        </div>
      </div>
    );
  };

  const showMobileDetailPage = isMobileView && isMobileDetailView && selectedBooking;
  const showDesktopDetails = !isMobileView && !!selectedBooking;

  return (
    <Layout>
      <div>
        <style>{`
          .booking-row-list {
            display: grid;
            gap: 12px;
          }

          .booking-row-item {
            display: grid;
            grid-template-columns: 1.25fr 1fr 1fr 1fr 0.9fr 0.8fr;
            align-items: center;
            gap: 14px;
            padding: 18px 18px;
            border-radius: 14px;
            background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
            border: 1px solid #e2e8f0;
            box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
            transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
          }

          .booking-row-item:hover {
            transform: translateY(-1px);
            border-color: #bfdbfe;
            box-shadow: 0 12px 22px rgba(14, 165, 233, 0.08);
          }

          .booking-row-item.clickable {
            cursor: pointer;
          }

          .booking-row-main {
            min-width: 0;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }

          .booking-row-customer {
            font-size: 14px;
            font-weight: 700;
            color: #0f172a;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 100%;
          }

          .booking-row-meta {
            margin-top: 4px;
            font-size: 11px;
            color: #64748b;
            letter-spacing: 0.02em;
          }

          .booking-row-service,
          .booking-row-date,
          .booking-row-amount,
          .booking-row-status {
            font-size: 13px;
            color: #334155;
            min-width: 0;
          }

          .booking-row-amount {
            font-weight: 700;
            color: #0f172a;
          }

          .booking-row-status-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 90px;
            padding: 6px 10px;
            border-radius: 999px;
            font-size: 10px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            border: 1px solid transparent;
          }

          .booking-list-header {
            display: grid;
            grid-template-columns: 1.25fr 1fr 1fr 1fr 0.9fr 0.8fr;
            gap: 14px;
            padding: 0 18px 10px;
            font-size: 11px;
            color: #64748b;
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
          }

          @media (min-width: 769px) {
            .booking-stats-grid {
              grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
              gap: 12px !important;
            }

            .booking-stat-card {
              padding: 14px 12px !important;
              min-height: 84px !important;
              gap: 8px !important;
            }

            .booking-stat-card > div:first-child {
              width: 34px !important;
              height: 34px !important;
              min-width: 34px !important;
            }

            .booking-stat-label {
              font-size: 11px !important;
            }

            .booking-stat-value {
              font-size: 20px !important;
              letter-spacing: 0.4px !important;
            }
          }

          @media (max-width: 860px) {
            .booking-row-item,
            .booking-list-header {
              grid-template-columns: repeat(3, minmax(0, 1fr));
            }
          }

          @media (max-width: 767px) {
            .booking-mobile-header {
              padding: 18px 16px !important;
            }

            .booking-mobile-header h1 {
              font-size: 22px !important;
              line-height: 1.2 !important;
            }

            .booking-mobile-header p {
              font-size: 13px !important;
            }

            .booking-mobile-badge {
              display: none !important;
            }

            .booking-stats-grid {
              display: grid !important;
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
              gap: 8px !important;
            }

            .booking-stat-card {
              flex: unset !important;
              min-width: 0 !important;
              padding: 12px 10px !important;
              min-height: 92px !important;
              border-radius: 12px !important;
              box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08) !important;
            }

            .booking-stat-label {
              font-size: 9px !important;
              line-height: 1.2 !important;
            }

            .booking-stat-value {
              font-size: 20px !important;
              line-height: 1.1 !important;
              letter-spacing: 0.1px !important;
            }

            .booking-filter-toolbar {
              padding: 14px !important;
              gap: 12px !important;
            }

            .booking-filter-toolbar > div {
              width: 100% !important;
              min-width: 0 !important;
            }

            .booking-filter-actions {
              width: 100% !important;
              display: flex !important;
              justify-content: space-between !important;
              gap: 8px !important;
            }

            .booking-filter-actions select {
              flex: 1 !important;
              font-size: 12px !important;
            }

            .booking-list-header {
              display: none !important;
            }

            .booking-row-item {
              grid-template-columns: 1fr !important;
              gap: 8px !important;
              padding: 14px 12px !important;
            }

            .booking-row-main, .booking-row-service, .booking-row-date, .booking-row-status {
              display: grid !important;
              grid-template-columns: 92px minmax(0, 1fr) !important;
              align-items: flex-start !important;
              gap: 8px !important;
            }

            .booking-row-amount {
              display: grid !important;
              grid-template-columns: 92px minmax(0, 1fr) auto !important;
              align-items: center !important;
              gap: 8px !important;
            }

            .booking-row-main::before,
            .booking-row-service::before,
            .booking-row-date::before,
            .booking-row-amount::before,
            .booking-row-status::before {
              content: attr(data-label);
              font-size: 10px !important;
              font-weight: 700 !important;
              letter-spacing: 0.08em !important;
              text-transform: uppercase !important;
              color: #64748b !important;
              line-height: 1.5 !important;
            }

            .booking-row-meta {
              margin-top: 2px !important;
              color: #64748b !important;
            }

            .booking-row-main .booking-row-customer,
            .booking-row-service,
            .booking-row-amount,
            .booking-row-status {
              min-width: 0 !important;
            }
          }
        `}</style>

        {showMobileDetailPage ? (
          <div>
            <div style={{ marginBottom: '18px' }}>
              <button
                onClick={handleBackToList}
                style={{
                  background: '#f1f5f9',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '9px 14px',
                  color: '#0f172a',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                ← Back to bookings
              </button>
            </div>
            {isLoadingDetails ? (
              <div style={{ padding: '20px 0', textAlign: 'center', color: '#64748b', fontSize: '16px' }}>
                Loading booking details...
              </div>
            ) : (
              renderBookingDetails(selectedBooking)
            )}
          </div>
        ) : (
          <div>
            <div className="booking-mobile-header" style={{
              background: 'linear-gradient(135deg, #14b8a6 0%, #0ea5e9 100%)',
              borderRadius: '14px',
              padding: '28px',
              marginBottom: '28px',
              boxShadow: '0 4px 16px 0 #14b8a622',
              color: 'white',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h1 style={{ margin: 0, fontSize: '32px', fontWeight: '700', display: 'flex', alignItems: 'center' }}>
                    <span style={{
                      marginRight: '15px',
                      width: '38px',
                      height: '38px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(255,255,255,0.22)',
                      borderRadius: '10px',
                      boxShadow: '0 2px 8px #14b8a622'
                    }}>
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#14b8a6" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    </span>
                    Booking Management
                  </h1>
                  <p style={{ margin: '8px 0 0 0', fontSize: '16px', opacity: 0.9 }}>
                    View and manage all bookings on the eLaba platform
                  </p>
                  <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.8 }}>
                    Total Bookings: {bookings.length}
                  </p>
                </div>
                <div className="booking-mobile-badge" style={{
                  background: 'linear-gradient(90deg, #2dd4bf 0%, #38bdf8 100%)',
                  padding: '15px 22px',
                  borderRadius: '12px',
                  fontSize: '24px',
                  color: '#fff',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 8px #0ea5e922',
                  letterSpacing: 2
                }}>
                  BOOKINGS
                </div>
              </div>
            </div>

            <div className="booking-stats-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
              gap: 28,
              marginBottom: 36,
            }}>
              <div className="booking-stat-card" style={{
                background: 'linear-gradient(120deg, #f8fafc 60%, #e0f2fe 100%)',
                borderRadius: 18,
                padding: '18px 22px',
                boxShadow: '0 4px 24px 0 #0ea5e91a',
                display: 'flex',
                alignItems: 'center',
                gap: 18,
                border: '1.5px solid #e0e7ef',
                minHeight: '96px',
              }}>
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px #0ea5e922',
                  flexShrink: 0,
                }}>
                  <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="booking-stat-label" style={{ fontSize: 13, color: '#334155', fontWeight: 600, opacity: 0.85, marginBottom: 2 }}>Total Bookings</div>
                  <div className="booking-stat-value" style={{ fontSize: 28, fontWeight: 800, color: '#0ea5e9' }}>{totalBookings}</div>
                </div>
              </div>

              <div className="booking-stat-card" style={{
                background: 'linear-gradient(135deg, rgba(240,253,244,1) 0%, rgba(187,247,208,1) 100%)',
                borderRadius: 18,
                padding: '18px 20px',
                boxShadow: '0 10px 24px rgba(34, 197, 94, 0.12)',
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                border: '1px solid rgba(134, 239, 172, 0.45)',
                minHeight: '96px',
              }}>
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #6ee7b7 0%, #34d399 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px #14b8a622',
                  flexShrink: 0,
                }}>
                  <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="#065f46" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="booking-stat-label" style={{ fontSize: 13, color: '#166534', fontWeight: 600, opacity: 0.85, marginBottom: 2 }}>Completed</div>
                  <div className="booking-stat-value" style={{ fontSize: 28, fontWeight: 800, color: '#22c55e' }}>{completedCount}</div>
                </div>
              </div>

              <div className="booking-stat-card" style={{
                background: 'linear-gradient(135deg, rgba(254,252,232,1) 0%, rgba(186,230,253,1) 100%)',
                borderRadius: 18,
                padding: '18px 20px',
                boxShadow: '0 10px 24px rgba(245, 158, 66, 0.12)',
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                border: '1px solid rgba(253, 224, 71, 0.4)',
                minHeight: '96px',
              }}>
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #fcd34d 0%, #38bdf8 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px #fde68a55',
                  flexShrink: 0,
                }}>
                  <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="6" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="booking-stat-label" style={{ fontSize: 13, color: '#92400e', fontWeight: 600, opacity: 0.85, marginBottom: 2 }}>Pending</div>
                  <div className="booking-stat-value" style={{ fontSize: 28, fontWeight: 800, color: '#f59e42' }}>{pendingCount}</div>
                </div>
              </div>

              <div className="booking-stat-card" style={{
                background: 'linear-gradient(135deg, rgba(254,242,242,1) 0%, rgba(252,165,165,1) 100%)',
                borderRadius: 18,
                padding: '18px 20px',
                boxShadow: '0 10px 24px rgba(239, 68, 68, 0.12)',
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                border: '1px solid rgba(254, 202, 202, 0.6)',
                minHeight: '96px',
              }}>
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #fca5a5 0%, #f87171 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px #fca5a555',
                  flexShrink: 0,
                }}>
                  <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="#991b1b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="booking-stat-label" style={{ fontSize: 13, color: '#991b1b', fontWeight: 600, opacity: 0.85, marginBottom: 2 }}>Cancelled</div>
                  <div className="booking-stat-value" style={{ fontSize: 28, fontWeight: 800, color: '#ef4444' }}>{cancelledCount}</div>
                </div>
              </div>
            </div>

            <div className="booking-filter-toolbar" style={{
              background: 'linear-gradient(135deg, #f0fdfa 0%, #e0f2fe 100%)',
              borderRadius: '13px',
              padding: '22px',
              marginBottom: '28px',
              boxShadow: '0 2px 8px #14b8a611',
              display: 'flex',
              gap: '20px',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <div style={{ flex: 1, minWidth: '300px' }}>
                <input
                  type="text"
                  placeholder="Search bookings by customer, shop, service, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s ease',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#3b82f6')}
                  onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                />
              </div>

              <div className="booking-filter-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Filter:</span>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="all">All Bookings</option>
                  <option value="pending">Pending Only</option>
                  <option value="completed">Completed Only</option>
                  <option value="cancelled">Cancelled Only</option>
                </select>
              </div>
            </div>

            <div className="booking-table-wrap" style={{ width: '100%', marginBottom: '30px' }}>
              {loading ? (
                <div style={{ textAlign: 'center', color: '#64748b', fontSize: 18, padding: 40 }}>Loading bookings...</div>
              ) : error ? (
                <div style={{ color: '#ef4444', textAlign: 'center', fontWeight: 500, fontSize: 17 }}>{error}</div>
              ) : filteredBookings.length > 0 ? (
                <div>
                  <div className="booking-list-header">
                    <div>Customer</div>
                    <div>Shop</div>
                    <div>Service</div>
                    <div>Booking Date</div>
                    <div>Status</div>
                    <div>Amount</div>
                  </div>

                  <div className="booking-row-list">
                    {filteredBookings.map((booking) => {
                      const customerName = ((booking.customer_first_name || '') + (booking.customer_last_name ? ' ' + booking.customer_last_name : '') || '-').trim();
                      const amount = Number(booking.total_amount || booking.amount || booking.price || 0);
                      const serviceText = booking.service_name || booking.service_id || 'Service';
                      const shopText = booking.shop_name || booking.shopId || 'Shop';
                      const statusValue = booking.booking_status || booking.status || 'pending';
                      const statusMeta = getStatusMeta(statusValue);
                      const bookingReference = booking.payment_reference || booking.booking_id || booking.id || 'No reference';

                      return (
                        <div
                          key={booking.id || booking.booking_id}
                          className={`booking-row-item ${!isMobileView ? 'clickable' : ''}`}
                          onClick={() => {
                            if (!isMobileView) {
                              handleView(booking);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (!isMobileView && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault();
                              handleView(booking);
                            }
                          }}
                          tabIndex={!isMobileView ? 0 : -1}
                          role={!isMobileView ? 'button' : undefined}
                          style={isMobileView ? { cursor: 'default' } : undefined}
                        >
                          <div className="booking-row-main" data-label="Customer">
                            <div className="booking-row-customer">{customerName !== '-' ? customerName : 'Guest Customer'}</div>
                            <div className="booking-row-meta">Ref: {bookingReference}</div>
                          </div>

                          <div className="booking-row-service" data-label="Shop">
                            {shopText}
                          </div>

                          <div className="booking-row-service" data-label="Service">
                            {serviceText}
                          </div>

                          <div className="booking-row-date" data-label="Booking Date">
                            {formatBookingDate(booking)}
                          </div>

                          <div className="booking-row-status" data-label="Status">
                            <span className="booking-row-status-badge" style={{
                              background: statusMeta.bg,
                              color: statusMeta.color,
                              borderColor: statusMeta.border,
                            }}>
                              {statusMeta.label}
                            </span>
                          </div>

                          <div className="booking-row-amount" data-label="Amount" style={isMobileView ? { display: 'grid', gridTemplateColumns: '92px minmax(0, 1fr) auto', alignItems: 'center', gap: '8px' } : undefined}>
                            <span>₱{formatAmount(amount)}</span>
                            {isMobileView && (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleView(booking, true);
                                }}
                                style={{
                                  border: '1px solid #14b8a6',
                                  borderRadius: '10px',
                                  background: '#ffffff',
                                  color: '#0f766e',
                                  cursor: 'pointer',
                                  padding: '7px 12px',
                                  fontSize: '12px',
                                  fontWeight: 700,
                                  justifySelf: 'end',
                                }}
                              >
                                View details
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {showDesktopDetails && (
                    <div style={{ marginTop: '18px' }}>
                      <div style={{ margin: '0 0 12px', color: '#0f172a', fontSize: '18px', fontWeight: 700 }}>
                        Booking details
                      </div>
                      {isLoadingDetails ? (
                        <div style={{ padding: '24px', textAlign: 'center', color: '#64748b', background: '#fff', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                          Loading booking details...
                        </div>
                      ) : (
                        renderBookingDetails(selectedBooking)
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    margin: '0 auto 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f1f5f9',
                    borderRadius: '50%'
                  }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  </div>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '20px', color: '#1e293b' }}>
                    {searchTerm || filterStatus !== 'all' ? 'No bookings found' : 'No bookings yet'}
                  </h3>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '16px' }}>
                    {searchTerm || filterStatus !== 'all' ? 'Try adjusting your search or filters' : 'Bookings will appear here as they are made on the platform'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
